"use client"

import { useState } from "react"

import {
  Check,
  FileAudio,
  Image as ImageIcon,
  Loader2,
  Music2,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react"

import type {
  ImportedRow,
  Playlist,
  Song,
} from "../../metadataTypes"

import {
  buttonClass,
  formatBytes,
} from "../../metadataUtils"

import {
  Field,
  Section,
  Stat,
} from "./metadataEditorParts"

import MetadataSongs from "./MetadataSongs"

type Props = {
  playlist: Playlist
  rows: ImportedRow[]
  selected?: ImportedRow
  selectedCount: number

  loading: boolean
  progress: number
  dragging: boolean
  error: string | null

  inputRef: React.RefObject<HTMLInputElement | null>

  setDragging: (value: boolean) => void

  processFiles: (
    files: FileList | File[]
  ) => Promise<void>

  changeRow: (
    id: string,
    patch: Partial<ImportedRow>
  ) => void

  changePlaylist: (
    patch: Partial<Playlist>
  ) => void

  changeSong: (
    index: number,
    patch: Partial<Song>
  ) => void

  addImported: () => void
  addManual: () => void

  removeRow: (id: string) => void
  removeSong: (index: number) => void

  moveSong: (
    index: number,
    direction: -1 | 1
  ) => void

  save: () => void
  saved: boolean

  selectRow: (id: string) => void
  closeSelected: () => void
}

export default function MetadataEditor({
  playlist,
  rows,
  selected,
  selectedCount,

  loading,
  progress,
  dragging,
  error,

  inputRef,

  setDragging,

  processFiles,
  changeRow,
  changePlaylist,
  changeSong,

  addImported,
  addManual,
  removeRow,
  removeSong,
  moveSong,

  save,
  saved,

  selectRow,
  closeSelected,
}: Props) {
  const [playlistOpen, setPlaylistOpen] =
    useState<boolean>(true)

  const [songsOpen, setSongsOpen] =
    useState<boolean>(true)

  return (
    <div className="space-y-1.5">

      {/* =====================================================
          IMPORTAR
      ===================================================== */}

      <Section
        icon={
          <Upload
            size={13}
            className="text-white/45"
          />
        }
        title="Importar"
        description={
          loading
            ? `Procesando ${progress}%`
            : "Añade música para extraer metadatos"
        }
        action={
          <button
            type="button"
            onClick={() =>
              inputRef.current?.click()
            }
            disabled={loading}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 text-[10px] font-medium text-black disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2
                  size={12}
                  className="animate-spin"
                />

                {progress}%
              </>
            ) : (
              <>
                <Plus size={12} />
                Añadir
              </>
            )}
          </button>
        }
      >
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,.mp3,.mpeg,.wav,.m4a,.flac,.ogg"
          multiple
          className="hidden"
          onChange={e => {
            if (e.target.files?.length) {
              processFiles(e.target.files)
            }

            e.currentTarget.value = ""
          }}
        />

        <div
          onDragOver={e => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => {
            setDragging(false)
          }}
          onDrop={e => {
            e.preventDefault()
            setDragging(false)

            if (e.dataTransfer.files.length) {
              processFiles(
                e.dataTransfer.files
              )
            }
          }}
          onClick={() =>
            inputRef.current?.click()
          }
          className={`m-1.5 flex cursor-pointer items-center gap-2.5 rounded-lg border border-dashed px-2.5 py-2.5 transition ${
            dragging
              ? "border-white/35 bg-white/[.07]"
              : "border-white/10 bg-black/10 hover:bg-white/[.035]"
          }`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[.05]">
            <FileAudio
              size={15}
              className="text-white/30"
            />
          </div>

          <div className="min-w-0">
            <p className="truncate text-[10px] text-white/60">
              Arrastra tus archivos aquí
            </p>

            <p className="mt-0.5 text-[8px] text-white/20">
              MP3 · WAV · M4A · FLAC · OGG
            </p>
          </div>

          <span className="ml-auto shrink-0 rounded-md border border-white/10 bg-white/[.04] px-2 py-1 text-[8px] text-white/40">
            Seleccionar
          </span>
        </div>

        {error && (
          <div className="mx-1.5 mb-1.5 rounded-lg border border-red-500/20 bg-red-500/10 px-2.5 py-2 text-[9px] text-red-300">
            {error}
          </div>
        )}
      </Section>

      {/* =====================================================
          IMPORTADOS
      ===================================================== */}

      {rows.length > 0 && (
        <Section
          icon={
            <FileAudio
              size={13}
              className="text-white/45"
            />
          }
          title="Importados"
          description={`${rows.length} archivos · ${selectedCount} seleccionados`}
          action={
            <button
              type="button"
              onClick={addImported}
              disabled={!selectedCount}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 text-[10px] font-medium text-black disabled:opacity-25"
            >
              <Plus size={12} />
              Añadir
            </button>
          }
        >
          <div className="grid gap-1 p-1 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map(row => {
              const active =
                row.id === selected?.id

              return (
                <div
                  key={row.id}
                  onClick={() =>
                    selectRow(row.id)
                  }
                  className={`group flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border p-1.5 transition ${
                    active
                      ? "border-white/25 bg-white/[.08]"
                      : "border-white/5 bg-black/10 hover:border-white/10 hover:bg-white/[.035]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={row.selected}
                    onClick={e =>
                      e.stopPropagation()
                    }
                    onChange={e =>
                      changeRow(row.id, {
                        selected:
                          e.target.checked,
                      })
                    }
                    className="h-3.5 w-3.5 shrink-0 accent-white"
                  />

                  {row.imageUrl ? (
                    <img
                      src={row.imageUrl}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/[.05]">
                      <Music2
                        size={14}
                        className="text-white/20"
                      />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[10px] text-white/75">
                      {row.title ||
                        "Sin título"}
                    </p>

                    <p className="truncate text-[9px] text-white/30">
                      {row.artist ||
                        "Artista desconocido"}
                    </p>

                    <p className="truncate text-[8px] text-white/15">
                      {row.fileName}
                    </p>
                  </div>

                  <div className="hidden shrink-0 text-right md:block">
                    <p className="text-[8px] text-white/25">
                      {row.trackTime ||
                        "--:--"}
                    </p>

                    <p className="text-[7px] text-white/15">
                      {formatBytes(
                        row.metadata.size
                      )}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      removeRow(row.id)
                    }}
                    className="shrink-0 rounded-md p-1.5 text-white/15 opacity-0 transition hover:bg-red-500/10 hover:text-red-300 group-hover:opacity-100"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )
            })}
          </div>
        </Section>
      )}

      {/* =====================================================
          EDITOR TRACK
      ===================================================== */}

      {selected && (
        <Section
          icon={
            <Music2
              size={13}
              className="text-white/45"
            />
          }
          title="Editar track"
          description={
            selected.title ||
            selected.fileName
          }
          action={
            <button
              type="button"
              onClick={closeSelected}
              className="rounded-lg p-1.5 text-white/25 hover:bg-white/[.06] hover:text-white"
            >
              <X size={14} />
            </button>
          }
        >
          <div className="grid gap-2.5 p-2.5 lg:grid-cols-[100px_minmax(0,1fr)]">

            {/* COVER */}

            <div>
              <div className="aspect-square overflow-hidden rounded-lg border border-white/10 bg-black/20">
                {selected.imageUrl ? (
                  <img
                    src={selected.imageUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <ImageIcon
                      size={25}
                      className="text-white/10"
                    />
                  </div>
                )}
              </div>

              <div className="mt-1 grid grid-cols-2 gap-1">
                <Stat
                  label="Duración"
                  value={
                    selected.trackTime ||
                    "--:--"
                  }
                />

                <Stat
                  label="Tamaño"
                  value={formatBytes(
                    selected.metadata.size
                  )}
                />
              </div>
            </div>

            {/* FIELDS */}

            <div className="grid content-start gap-1.5 sm:grid-cols-2">
              <Field
                label="Título"
                value={selected.title}
                onChange={value =>
                  changeRow(selected.id, {
                    title: value,
                  })
                }
              />

              <Field
                label="Artista"
                value={selected.artist}
                onChange={value =>
                  changeRow(selected.id, {
                    artist: value,
                  })
                }
              />

              <Field
                label="Álbum"
                value={selected.album}
                onChange={value =>
                  changeRow(selected.id, {
                    album: value,
                  })
                }
              />

              <Field
                label="Año"
                value={selected.year}
                onChange={value =>
                  changeRow(selected.id, {
                    year: value,
                  })
                }
              />

              <Field
                label="Género"
                value={selected.genre}
                onChange={value =>
                  changeRow(selected.id, {
                    genre: value,
                  })
                }
              />

              <Field
                label="Link"
                value={selected.link}
                onChange={value =>
                  changeRow(selected.id, {
                    link: value,
                  })
                }
              />

              <Field
                label="Imagen"
                value={selected.songimg}
                onChange={value =>
                  changeRow(selected.id, {
                    songimg: value,
                  })
                }
                className="sm:col-span-2"
              />

              {/* AUDIO INFO */}

              <div className="grid grid-cols-4 gap-1 sm:col-span-2">
                <Stat
                  label="Bitrate"
                  value={
                    selected.metadata.bitrate
                      ? `${Math.round(
                          selected.metadata.bitrate /
                            1000
                        )}k`
                      : "—"
                  }
                />

                <Stat
                  label="Sample"
                  value={
                    selected.metadata
                      .sampleRate
                      ? `${selected.metadata.sampleRate}Hz`
                      : "—"
                  }
                />

                <Stat
                  label="Canales"
                  value={
                    selected.metadata.channels
                      ? String(
                          selected.metadata.channels
                        )
                      : "—"
                  }
                />

                <Stat
                  label="Formato"
                  value={
                    selected.metadata.mimeType
                      .replace(
                        "audio/",
                        ""
                      )
                      .toUpperCase() || "—"
                  }
                />
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* =====================================================
          PLAYLIST
      ===================================================== */}

      <Section
        icon={
          <Music2
            size={13}
            className="text-white/45"
          />
        }
        title="Playlist"
        description={`${playlist.playlistData.length} canciones`}
        collapsible
        open={playlistOpen}
        onToggle={() =>
          setPlaylistOpen(
            (previous: boolean) =>
              !previous
          )
        }
        action={
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              addManual()
            }}
            className={`${buttonClass} h-8 shrink-0 px-2.5 text-[10px]`}
          >
            <Plus size={12} />
            Manual
          </button>
        }
      >
        <div className="grid gap-1.5 p-1.5 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Título"
            value={playlist.title}
            onChange={value =>
              changePlaylist({
                title: value,
              })
            }
          />

          <Field
            label="Link"
            value={playlist.link}
            onChange={value =>
              changePlaylist({
                link: value,
              })
            }
          />

          <Field
            label="Artista"
            value={playlist.artist}
            onChange={value =>
              changePlaylist({
                artist: value,
              })
            }
          />

          <Field
            label="Imagen"
            value={playlist.imgUrl}
            onChange={value =>
              changePlaylist({
                imgUrl: value,
              })
            }
          />
        </div>
      </Section>

      {/* =====================================================
          CANCIONES
      ===================================================== */}

      {playlist.playlistData.length > 0 && (
        <Section
          icon={
            <Music2
              size={13}
              className="text-white/45"
            />
          }
          title="Canciones"
          description={`${playlist.playlistData.length} tracks`}
          collapsible
          open={songsOpen}
          onToggle={() =>
            setSongsOpen(
              (previous: boolean) =>
                !previous
            )
          }
          action={
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                save()
              }}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 text-[10px] font-medium text-black"
            >
              {saved ? (
                <>
                  <Check size={12} />
                  Guardado
                </>
              ) : (
                <>
                  <Save size={12} />
                  Guardar
                </>
              )}
            </button>
          }
        >
          <MetadataSongs
            playlist={playlist}
            changeSong={changeSong}
            removeSong={removeSong}
            moveSong={moveSong}
          />
        </Section>
      )}

      {/* =====================================================
          GUARDAR
      ===================================================== */}

      {/* <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3 text-xs font-medium text-black transition hover:bg-white/90"
        >
          <Save size={13} />

          {saved
            ? "Cambios guardados"
            : "Guardar playlist"}
        </button>
      </div> */}
    </div>
  )
}